import { Box } from '@mui/material';
import React from 'react';
import { Draggable } from '@hello-pangea/dnd';

import { styles } from './ReorderableBlockListItem.styles';

export type IReorderableBlockListItemProps = {
  /** Text displayed to label item. */
  label?: string;
  /** Value for key attribute on draggable object. */
  id: string;
  /** Value for index attribute on draggable object. Injected by parent. */
  index?: number;
  selected?: boolean;
  /** Injected by parent: true while a sibling is being dragged. */
  isDraggingOver?: boolean;
  /** Used instead of label to render custom children. */
  children?: React.ReactNode;
  /** Callback when item is clicked. */
  onClick?: () => void;
  dataTest?: string;
};

export const ReorderableBlockListItem: React.FC<IReorderableBlockListItemProps> = ({
  id,
  index,
  label,
  children,
  dataTest,
  selected,
  isDraggingOver,
  onClick
}) => {
  // `index` is injected by ReorderableBlockList via cloneElement — it's required by Draggable.
  if (typeof index !== 'number') {
    return null;
  }

  return (
    <Box sx={isDraggingOver ? { ...styles.container, ...styles.activeContainer } : styles.container}>
      <Box sx={styles.paddingContainer}>
        <Draggable key={id} draggableId={id} index={index}>
          {(provided, snapshot) => {
            const cardSx = {
              ...styles.card,
              ...(snapshot.isDragging ? styles.cardDragging : {}),
              ...(selected ? styles.cardSelected : {})
            };
            return (
              <Box
                role="button"
                sx={cardSx}
                ref={provided.innerRef}
                onClick={onClick}
                data-test={dataTest}
                {...provided.draggableProps}
                {...provided.dragHandleProps}
              >
                <Box sx={styles.cardContent}>{children || label}</Box>
                <Box>
                  <Box className="ReorderableBlockListItem-dragHandle" sx={styles.dragHandle} />
                </Box>
              </Box>
            );
          }}
        </Draggable>
      </Box>
    </Box>
  );
};
